/* ==========================================================================
   Plug Pro: Holiday 2026
   Custom elements for the Christmas homepage. No dependencies.
     <pp-lights>    string lights drawn as SVG (swag, line or frame)
     <pp-snow>      falling snow on a single canvas
     <pp-countdown> live countdown to Christmas Day
     <pp-atc>       add to cart that opens Dawn's cart drawer
   Several sections load this file; the guard keeps it to one run.
   ========================================================================== */
(function () {
  if (window.__ppXmas) return;
  window.__ppXmas = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SVGNS = 'http://www.w3.org/2000/svg';
  var uid = 0;

  function rand(a, b) {
    return a + Math.random() * (b - a);
  }

  function svgEl(name, attrs, parent) {
    var node = document.createElementNS(SVGNS, name);
    for (var key in attrs) node.setAttribute(key, attrs[key]);
    if (parent) parent.appendChild(node);
    return node;
  }

  // Pause animations while an element is off screen.
  var visibility =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              entry.target.classList.toggle('is-paused', !entry.isIntersecting);
            });
          },
          { rootMargin: '160px 0px' }
        )
      : null;

  /* ------------------------------------------------------------------------
     <pp-lights>
     data-shape    swag | line | frame
     data-spacing  px between bulbs (default 42)
     data-size     bulb scale (default 1)
     data-sag      drop of each drape in px
     data-drape    width of each drape in px
     data-pattern  w = warm white, o = orange (default "wwo")
     data-mode     twinkle | chase
     data-plug     "true" adds a plug and cable (frame only)
     data-power    "true" plays the power-on sequence once
     ------------------------------------------------------------------------ */
  var PALETTE = {
    w: { glass: ['#fffdf6', '#ffe3ae', '#e9a64c'], halo: '#ffcf86' },
    o: { glass: ['#fff1e0', '#ff9a3c', '#c24f00'], halo: '#ff6a00' }
  };

  class PPLights extends HTMLElement {}

  PPLights.prototype.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;
    this._id = 'ppl' + ++uid;
    this._w = 0;
    this._h = 0;
    var self = this;
    var measure = function () {
      var r = self.getBoundingClientRect();
      var w = Math.round(r.width);
      var h = Math.round(r.height);
      if (!w || !h) return;
      if (Math.abs(w - self._w) > 6 || Math.abs(h - self._h) > 6) {
        self._w = w;
        self._h = h;
        self.render();
      }
    };
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(measure);
      this._ro.observe(this);
    } else {
      window.addEventListener('resize', measure);
    }
    measure();
    if (visibility) visibility.observe(this);
  };

  PPLights.prototype.disconnectedCallback = function () {
    if (this._ro) this._ro.disconnect();
    if (visibility) visibility.unobserve(this);
    if (this._anims) this._anims.forEach(function (a) { a.cancel(); });
  };

  PPLights.prototype.pathFor = function (shape, w, h, size) {
    var d = '';
    if (shape === 'frame') {
      var pad = 30;
      var r = Math.min(parseFloat(this.dataset.radius) || 26, (w - pad * 2) / 2, (h - pad * 2) / 2);
      var x0 = pad, y0 = pad, x1 = w - pad, y1 = h - pad;
      d =
        'M' + (x0 + r) + ' ' + y0 +
        ' H' + (x1 - r) + ' Q' + x1 + ' ' + y0 + ' ' + x1 + ' ' + (y0 + r) +
        ' V' + (y1 - r) + ' Q' + x1 + ' ' + y1 + ' ' + (x1 - r) + ' ' + y1 +
        ' H' + (x0 + r) + ' Q' + x0 + ' ' + y1 + ' ' + x0 + ' ' + (y1 - r) +
        ' V' + (y0 + r) + ' Q' + x0 + ' ' + y0 + ' ' + (x0 + r) + ' ' + y0 + ' Z';
      return { d: d, closed: true };
    }
    var isLine = shape === 'line';
    var top = isLine ? Math.max(2, h * 0.12) : 0;
    var sag = parseFloat(this.dataset.sag) || (isLine ? Math.max(6, h * 0.32) : 44);
    var drape = parseFloat(this.dataset.drape) || (isLine ? 210 : 300);
    var count = Math.max(2, Math.round(w / drape));
    var step = w / count;
    d = 'M0 ' + top;
    for (var i = 0; i < count; i++) {
      var xa = i * step;
      var xb = (i + 1) * step;
      var drop = sag * (0.85 + 0.3 * ((i * 37) % 7) / 7);
      d += ' Q' + (xa + step / 2) + ' ' + (top + drop * 2) + ' ' + xb + ' ' + top;
    }
    return { d: d, closed: false };
  };

  PPLights.prototype.render = function () {
    var w = this._w, h = this._h;
    var shape = this.dataset.shape || 'swag';
    var size = parseFloat(this.dataset.size) || 1;
    if (w < 480) size *= 0.82;
    var spacing = (parseFloat(this.dataset.spacing) || 42) * (w < 480 ? 0.9 : 1);
    var pattern = (this.dataset.pattern || 'wwo').split('');
    var chase = this.dataset.mode === 'chase';
    var id = this._id;

    if (this._anims) this._anims.forEach(function (a) { a.cancel(); });
    this._anims = [];
    this.textContent = '';

    var svg = svgEl('svg', { viewBox: '0 0 ' + w + ' ' + h, width: w, height: h, 'aria-hidden': 'true', focusable: 'false' });
    var defs = svgEl('defs', {}, svg);
    Object.keys(PALETTE).forEach(function (k) {
      var p = PALETTE[k];
      var glass = svgEl('radialGradient', { id: id + 'g' + k, cx: '42%', cy: '40%', r: '70%' }, defs);
      svgEl('stop', { offset: '0%', 'stop-color': p.glass[0] }, glass);
      svgEl('stop', { offset: '45%', 'stop-color': p.glass[1] }, glass);
      svgEl('stop', { offset: '100%', 'stop-color': p.glass[2] }, glass);
      var halo = svgEl('radialGradient', { id: id + 'h' + k }, defs);
      svgEl('stop', { offset: '0%', 'stop-color': p.halo, 'stop-opacity': '0.75' }, halo);
      svgEl('stop', { offset: '45%', 'stop-color': p.halo, 'stop-opacity': '0.22' }, halo);
      svgEl('stop', { offset: '100%', 'stop-color': p.halo, 'stop-opacity': '0' }, halo);
    });

    var geo = this.pathFor(shape, w, h, size);
    var wire = svgEl('path', { d: geo.d, class: 'ppx-wire' }, svg);
    this.appendChild(svg);

    var total = wire.getTotalLength();
    var n = Math.max(3, Math.floor(total / spacing));
    var step = total / n;
    var cx = w / 2, cy = h / 2;
    var bulbs = svgEl('g', {}, svg);
    var list = [];

    for (var i = 0; i < n; i++) {
      var s = geo.closed ? i * step : step / 2 + i * step;
      if (!geo.closed && s > total) break;
      var p = wire.getPointAtLength(s);
      var a = wire.getPointAtLength(Math.max(0, s - 1));
      var b = wire.getPointAtLength(Math.min(total, s + 1));
      var tx = b.x - a.x, ty = b.y - a.y;
      var len = Math.hypot(tx, ty) || 1;
      tx /= len; ty /= len;
      var angle;
      if (geo.closed) {
        var nx = -ty, ny = tx;
        if (nx * (p.x - cx) + ny * (p.y - cy) < 0) { nx = -nx; ny = -ny; }
        angle = Math.atan2(-nx, ny) * 180 / Math.PI + rand(-6, 6);
      } else {
        angle = Math.atan2(ty, tx) * 180 / Math.PI * 0.35 + rand(-12, 12);
      }
      var key = pattern[i % pattern.length] === 'o' ? 'o' : 'w';
      list.push(this.bulb(bulbs, p.x, p.y, angle, size, key, i, n, chase));
    }

    if (this.dataset.plug === 'true' && geo.closed) this.plug(svg, w, h, size);

    if (!reduceMotion && typeof wire.animate === 'function') {
      var current = svgEl('path', { d: geo.d, class: 'ppx-current' }, svg);
      svg.insertBefore(current, bulbs);
      var seg = Math.min(70, total * 0.08);
      current.style.strokeDasharray = seg + ' ' + total;
      this._anims.push(
        current.animate(
          [{ strokeDashoffset: total + seg }, { strokeDashoffset: 0 }],
          { duration: Math.max(2800, total * 5.5), iterations: Infinity, easing: 'linear' }
        )
      );
      if (this.dataset.power === 'true' && !this._powered) {
        this._powered = true;
        this.powerOn(list);
      }
    }
  };

  PPLights.prototype.bulb = function (parent, x, y, angle, s, key, i, n, chase) {
    var id = this._id;
    var g = svgEl('g', { class: 'ppx-b', transform: 'translate(' + x.toFixed(1) + ' ' + y.toFixed(1) + ') rotate(' + angle.toFixed(1) + ')' }, parent);
    var dur = chase ? 1.9 : rand(1.8, 3.6);
    var delay = chase ? -((n - i) / n) * dur * 2 : -rand(0, 3.6);
    g.style.setProperty('--d', dur.toFixed(2) + 's');
    g.style.setProperty('--dl', delay.toFixed(2) + 's');
    svgEl('circle', { class: 'ppx-b__halo', cx: 0, cy: 10.5 * s, r: 17 * s, fill: 'url(#' + id + 'h' + key + ')' }, g);
    svgEl('rect', { x: -2.6 * s, y: -1.2 * s, width: 5.2 * s, height: 4.6 * s, rx: 1.2 * s, fill: '#2b2b30' }, g);
    svgEl('path', {
      d: 'M0 ' + 3 * s +
        ' C' + 4.3 * s + ' ' + 3 * s + ' ' + 5.6 * s + ' ' + 8.4 * s + ' ' + 5.6 * s + ' ' + 11 * s +
        ' C' + 5.6 * s + ' ' + 14.2 * s + ' ' + 3 * s + ' ' + 16.2 * s + ' 0 ' + 16.2 * s +
        ' C' + -3 * s + ' ' + 16.2 * s + ' ' + -5.6 * s + ' ' + 14.2 * s + ' ' + -5.6 * s + ' ' + 11 * s +
        ' C' + -5.6 * s + ' ' + 8.4 * s + ' ' + -4.3 * s + ' ' + 3 * s + ' 0 ' + 3 * s + 'Z',
      fill: 'url(#' + id + 'g' + key + ')'
    }, g);
    svgEl('circle', { class: 'ppx-b__core', cx: 0, cy: 11 * s, r: 2.6 * s, fill: '#fffaf0' }, g);
    svgEl('ellipse', { cx: -2.1 * s, cy: 8.2 * s, rx: 1 * s, ry: 2.5 * s, fill: '#ffffff', opacity: 0.7 }, g);
    return g;
  };

  PPLights.prototype.plug = function (svg, w, h, s) {
    var pad = 30;
    var x = pad + (w - pad * 2) * 0.16;
    var y = h - pad;
    var ex = x - 32 * s;
    var ey = y + 38 * s;
    var cable = 'M' + x + ' ' + y + ' C' + x + ' ' + (y + 20 * s) + ' ' + (ex + 14 * s) + ' ' + (ey - 18 * s) + ' ' + ex + ' ' + ey;
    var cablePath = svgEl('path', { d: cable, class: 'ppx-wire', 'stroke-width': 2.4 }, svg);
    var g = svgEl('g', { transform: 'translate(' + ex + ' ' + ey + ') rotate(38)' }, svg);
    svgEl('rect', { x: -7 * s, y: 0, width: 14 * s, height: 18 * s, rx: 3 * s, fill: '#1b1b20', stroke: '#3a3a42', 'stroke-width': 1 }, g);
    svgEl('rect', { x: -4.4 * s, y: 17 * s, width: 2.2 * s, height: 8 * s, rx: 0.8 * s, fill: '#ff8a30' }, g);
    svgEl('rect', { x: 2.2 * s, y: 17 * s, width: 2.2 * s, height: 8 * s, rx: 0.8 * s, fill: '#ff8a30' }, g);
    svgEl('circle', { cx: 0, cy: 28 * s, r: 14 * s, fill: 'url(#' + this._id + 'ho)' }, g);
    if (!reduceMotion && typeof cablePath.animate === 'function') {
      var len = cablePath.getTotalLength();
      var pulse = svgEl('path', { d: cable, class: 'ppx-current' }, svg);
      pulse.style.strokeDasharray = '14 ' + len;
      this._anims.push(
        pulse.animate([{ strokeDashoffset: -len }, { strokeDashoffset: 14 }], {
          duration: 1400,
          iterations: Infinity,
          easing: 'ease-in'
        })
      );
    }
  };

  // Bulbs light up one after another, as if the plug just went in.
  PPLights.prototype.powerOn = function (list) {
    var self = this;
    self.classList.add('is-powering');
    var total = list.length;
    list.forEach(function (bulb, i) {
      setTimeout(function () { bulb.classList.add('is-on'); }, 450 + i * (1100 / total));
    });
    setTimeout(function () { self.classList.remove('is-powering'); }, 1800);
  };

  if (!customElements.get('pp-lights')) customElements.define('pp-lights', PPLights);

  /* ------------------------------------------------------------------------
     <pp-snow data-density="low|medium|high" data-scope="page|hero">
     ------------------------------------------------------------------------ */
  class PPSnow extends HTMLElement {
    connectedCallback() {
      if (this._ready || reduceMotion) return;
      if (document.querySelector('canvas.ppx-snow')) return;
      this._ready = true;
      var host = this.dataset.scope === 'hero' ? this.closest('.ppx-hero') : null;
      var canvas = document.createElement('canvas');
      canvas.className = 'ppx-snow' + (host ? ' ppx-snow--hero' : '');
      canvas.setAttribute('aria-hidden', 'true');
      (host || document.body).appendChild(canvas);
      var ctx = canvas.getContext('2d');
      var area = { low: 26000, medium: 16000, high: 10000 }[this.dataset.density] || 16000;
      var w = 0, h = 0, flakes = [], raf = 0;

      var make = function (anywhere) {
        return {
          x: Math.random() * w,
          y: anywhere ? Math.random() * h : -6,
          r: rand(0.6, 2.3),
          vy: rand(0.25, 0.85),
          vx: rand(-0.15, 0.15),
          a: rand(0.25, 0.8),
          p: Math.random() * 6.28,
          ps: rand(0.004, 0.012)
        };
      };

      var resize = function () {
        var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        var box = host ? host.getBoundingClientRect() : { width: window.innerWidth, height: window.innerHeight };
        w = box.width;
        h = box.height;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        var count = Math.min(130, Math.round((w * h) / area));
        while (flakes.length < count) flakes.push(make(true));
        flakes.length = count;
      };

      var frame = function () {
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = '#ffffff';
        for (var i = 0; i < flakes.length; i++) {
          var f = flakes[i];
          f.p += f.ps;
          f.y += f.vy * (1 + f.r * 0.25);
          f.x += f.vx + Math.sin(f.p) * 0.3;
          if (f.y > h + 6) flakes[i] = f = make(false);
          if (f.x > w + 6) f.x = -6;
          if (f.x < -6) f.x = w + 6;
          ctx.globalAlpha = f.a;
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.r, 0, 6.2832);
          ctx.fill();
        }
        raf = requestAnimationFrame(frame);
      };

      var running = false;
      var start = function () { if (!running) { running = true; raf = requestAnimationFrame(frame); } };
      var stop = function () { running = false; cancelAnimationFrame(raf); };

      resize();
      window.addEventListener('resize', resize);
      document.addEventListener('visibilitychange', function () { document.hidden ? stop() : start(); });
      if (host && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (e) { e[0].isIntersecting ? start() : stop(); }).observe(host);
      } else {
        start();
      }
    }
  }
  if (!customElements.get('pp-snow')) customElements.define('pp-snow', PPSnow);

  /* ------------------------------------------------------------------------
     <pp-countdown data-date="2026-12-25">
     Counts to local midnight on that date in the visitor's own time zone.
     ------------------------------------------------------------------------ */
  class PPCountdown extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var parts = (this.dataset.date || '2026-12-25').split('-').map(Number);
      this.target = new Date(parts[0], (parts[1] || 12) - 1, parts[2] || 25, 0, 0, 0).getTime();
      this.units = {};
      var self = this;
      ['days', 'hours', 'minutes', 'seconds'].forEach(function (u) {
        self.units[u] = self.querySelector('[data-unit="' + u + '"]');
      });
      this.grid = this.querySelector('[data-grid]');
      this.done = this.querySelector('[data-done]');
      this.tick = this.tick.bind(this);
      this.tick();
      this.timer = setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      var diff = this.target - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        if (this.grid) this.grid.hidden = true;
        if (this.done) this.done.hidden = false;
        return;
      }
      var days = Math.floor(diff / 86400000);
      diff -= days * 86400000;
      var hours = Math.floor(diff / 3600000);
      diff -= hours * 3600000;
      var minutes = Math.floor(diff / 60000);
      diff -= minutes * 60000;
      this.set('days', days);
      this.set('hours', hours);
      this.set('minutes', minutes);
      this.set('seconds', Math.floor(diff / 1000));
    }

    set(unit, value) {
      var el = this.units[unit];
      if (!el) return;
      var text = String(value).padStart(2, '0');
      if (el.textContent === text) return;
      el.textContent = text;
      if (!reduceMotion && unit !== 'seconds') {
        el.classList.remove('is-tick');
        void el.offsetWidth;
        el.classList.add('is-tick');
      }
    }
  }
  if (!customElements.get('pp-countdown')) customElements.define('pp-countdown', PPCountdown);

  /* ------------------------------------------------------------------------
     <pp-atc> wraps a standard Shopify product form.
     With Dawn's cart drawer present it adds over AJAX and opens the drawer;
     otherwise the form submits normally to /cart/add.
     ------------------------------------------------------------------------ */
  class PPAtc extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (!cart || typeof cart.renderContents !== 'function' || !window.routes) return;
      event.preventDefault();
      if (this.button.getAttribute('aria-disabled') === 'true') return;

      var self = this;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('is-loading');
      if (this.error) this.error.hidden = true;

      var data = new FormData(this.form);
      try {
        data.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        data.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      } catch (e) {}

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: data
      })
        .then(function (response) { return response.json(); })
        .then(function (res) {
          if (res.status) {
            self.showError(res.description || res.message || 'This item could not be added to your cart.');
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-xmas-deals', productVariantId: data.get('id'), cartData: res });
          }
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(res);
          self.button.classList.add('is-added');
          setTimeout(function () { self.button.classList.remove('is-added'); }, 1800);
        })
        .catch(function () {
          self.showError('Your cart did not update. Check your connection and try again.');
        })
        .finally(function () {
          self.button.classList.remove('is-loading');
          self.button.removeAttribute('aria-disabled');
        });
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }
  }
  if (!customElements.get('pp-atc')) customElements.define('pp-atc', PPAtc);
})();
